import type { ParadigmsPageLocale } from '../types'

export const enParadigms: ParadigmsPageLocale = {
  sectionTag: 'Paradigms',
  heroTitleA: '10 ways ',
  heroTitleSpan: 'Ontology + AI',
  heroTitleB: ' shows up in production',
  heroSubtitle:
    'Beyond the alarm-driven response in the demo, ontology + AI shows up in a handful of recurring shapes — grouped here by what triggers them and what they produce. Same semantic foundation, very different deployments.',
  groupLabels: {
    input: 'Input-triggered',
    runtime: 'Change & runtime',
    output: 'Composite output',
  },
  groupDescriptions: {
    input: 'Paradigms that start from a user signal — an event, a file, a question, a stated goal.',
    runtime: 'Paradigms that wake up when rules change, when conditions drift, or when other agents post on the graph.',
    output: 'Paradigms that compose a richer artefact than a single action — a score, an audit chain, a recommendation.',
  },
  scenariosLabel: 'Typical scenarios',
  interactionLabel: 'Interaction',
  bottomCta: {
    sectionTag: 'See them in motion',
    titleLine1: 'A few of these paradigms are already live in the demo',
    titleLine2: 'More will land in upcoming releases',
    body: 'The current demo focuses on Goal-driven Planning, Continuous Conditions (a slice of What-if), and Explainability via reverse attribution. Try them, then come back and tell us which paradigms you want next.',
    button: 'Enter Demo',
  },
  paradigms: [
    {
      id: 'event-driven',
      number: '01',
      group: 'input',
      title: 'Event-driven Response',
      tagline:
        'A real-time signal hits a constraint; the ontology synthesises the response path on the fly.',
      triggerBadge: 'Event',
      outputBadge: 'Action chain',
      scenarios: [
        { industry: 'Petroleum', example: 'H₂S concentration spikes on a sour-gas pump → re-check material compliance, invite alternate suppliers, flag MOC.' },
        { industry: 'Finance', example: 'Suspected-fraud transaction → freeze + re-KYC + hand off to the relationship manager.' },
        { industry: 'Healthcare', example: 'Patient vital-sign breach → ranked differential diagnoses + required tests + drug contraindications.' },
        { industry: 'Manufacturing', example: 'In-line quality defect → traceback to batch + process + recall scope + supplier-claim packet.' },
      ],
      interactionNotes: [
        'Stream-shaped — every event is re-evaluated against the live graph.',
        'The response path is synthesised at fire-time, not stored as an SOP.',
        'The same event surfaces a different path when goals or hard constraints change.',
      ],
    },
    {
      id: 'document-driven',
      number: '02',
      group: 'input',
      title: 'Document-driven Synthesis',
      tagline:
        'Drop in a PDF, email, or spreadsheet; the ontology extracts entities and synthesises the handling chain.',
      triggerBadge: 'Document',
      outputBadge: 'Action chain',
      scenarios: [
        { industry: 'Legal', example: 'Contract upload → clause extraction + risk score + approval route + sign-off checklist.' },
        { industry: 'Procurement', example: 'RFQ ingestion → compliance matrix vs. spec + supplier AVL status + cost-quality ranking.' },
        { industry: 'Insurance', example: 'First-notice-of-loss packet → injury/damage classification + fraud screen + claim-handler routing.' },
        { industry: 'Healthcare', example: 'Patient referral packet → triage urgency + specialist match + pre-admission orders.' },
      ],
      interactionNotes: [
        'The LLM does the extraction; the ontology constrains and grounds the entities so the path is type-correct.',
        'Same paradigm whether the input is one PDF or a nightly batch of 500.',
        'Failed extractions surface as gaps in the synthesised path — easy to spot and patch.',
      ],
    },
    {
      id: 'question-grounded',
      number: '03',
      group: 'input',
      title: 'Question-grounded Q&A',
      tagline:
        'Natural-language questions answered along the graph itself — every claim is clickable back to its data source.',
      triggerBadge: 'Question',
      outputBadge: 'Grounded answer',
      scenarios: [
        { industry: 'Compliance', example: '"Can I award contract X this week?" → walks party + constraints + signing-authority graph.' },
        { industry: 'Healthcare', example: '"Should patient X start drug Y?" → diagnoses + current drugs + interactions + guidelines.' },
        { industry: 'Operations', example: '"Why is order 29117 delayed?" → multi-system traversal returns root cause + responsible party + ETA.' },
        { industry: 'Sales', example: '"Which accounts are at renewal risk?" → ranks accounts along usage + support + churn graphs.' },
      ],
      interactionNotes: [
        'Beats vanilla RAG: answers carry an entity-link chain, not just doc snippets.',
        'Click any entity in the answer to drill into its sub-graph.',
        'Disagreements logged back as proposed constraint changes — the system learns from corrections.',
      ],
    },
    {
      id: 'goal-driven',
      number: '04',
      group: 'input',
      title: 'Goal-driven Planning',
      tagline:
        'State the target; the ontology back-plans the action chain that satisfies every hard constraint.',
      triggerBadge: 'Goal',
      outputBadge: 'Plan',
      scenarios: [
        { industry: 'Procurement', example: '"Compliant seal for P-1023 with 2-week ETA" → reverse-plan through supplier / cert / MOC graph.' },
        { industry: 'Learning', example: '"Become a data analyst in 12 weeks" → courses + projects + assessments scheduled.' },
        { industry: 'Clinical', example: '"Stabilise this patient for surgery" → ordered protocol + contraindications + monitoring plan.' },
        { industry: 'Sales', example: '"Hit Q3 quota with current pipeline" → outreach plan + content + meeting cadence.' },
      ],
      interactionNotes: [
        'Backward chaining — the user sees the *why* of every step.',
        'The plan adapts live when conditions or constraints change.',
        'Multiple goals can be active; the ontology returns the Pareto frontier.',
      ],
    },
    {
      id: 'change-propagation',
      number: '05',
      group: 'runtime',
      title: 'Change Propagation',
      tagline:
        'Edit one rule, the graph fan-outs to every asset, contract, or process affected — with an auto-drafted change log.',
      triggerBadge: 'Rule update',
      outputBadge: 'Impact set',
      scenarios: [
        { industry: 'Energy', example: 'NACE H₂S threshold update → list of affected equipment + in-flight RFQs flagged.' },
        { industry: 'Finance', example: 'Central-bank rate change → revaluate every mortgage PD + flag high-risk accounts.' },
        { industry: 'Legal', example: 'New data-protection regulation → contracts needing clause revision + processes needing redesign.' },
        { industry: 'Real estate', example: 'City purchase-limit policy update → re-price assets + flag affected leases.' },
      ],
      interactionNotes: [
        'The killer feature against SOP-based systems — one edit, full-graph propagation.',
        'Before-and-after diff view of the graph is built in.',
        'Every propagation produces an audit-ready change log automatically.',
      ],
    },
    {
      id: 'what-if',
      number: '06',
      group: 'runtime',
      title: 'What-if Simulation',
      tagline:
        'Propose a hypothetical condition; the ontology runs the forward projection along causal chains.',
      triggerBadge: 'Hypothetical',
      outputBadge: 'Projection',
      scenarios: [
        { industry: 'Energy', example: '"If H₂S rises 30% over 6 months" → which assets cross SLA, which suppliers fall out.' },
        { industry: 'Finance', example: '"If rates rise 50 bp" → portfolio risk by segment, loan-loss provisions.' },
        { industry: 'Operations', example: '"If we double production" → bottlenecks + supplier-capacity gaps + skills gaps.' },
        { industry: 'Government', example: '"If subsidy increases 20%" → eligible population + budget impact + departments triggered.' },
      ],
      interactionNotes: [
        'Time-aware projection — not just instantaneous re-evaluation.',
        'Lets stakeholders compare scenarios side-by-side on the same graph.',
        'Distinguishes "constraint will fire" from "constraint is at risk" — risk colour-coded.',
      ],
    },
    {
      id: 'continuous-audit',
      number: '07',
      group: 'runtime',
      title: 'Continuous Audit',
      tagline:
        'A scheduled or streaming pass that watches for drift, anomalies, and silently broken constraints.',
      triggerBadge: 'Schedule',
      outputBadge: 'Drift report',
      scenarios: [
        { industry: 'Energy', example: 'Nightly compliance scan across all assets vs. current standards.' },
        { industry: 'Finance', example: 'Cross-system reconciliation — the same trade in different systems must agree.' },
        { industry: 'Healthcare', example: 'Patient cohorts watched against the latest guidelines; deviations flagged.' },
        { industry: 'Government', example: 'Monthly cross-agency data-consistency audit on the citizen graph.' },
      ],
      interactionNotes: [
        'Different from event-driven: runs continuously rather than on a single signal.',
        'Aggregates into one drift dashboard instead of a flood of independent alerts.',
        'Each finding includes its contributing nodes and a one-click "draft fix" action.',
      ],
    },
    {
      id: 'multi-agent',
      number: '08',
      group: 'runtime',
      title: 'Multi-agent Shared Workspace',
      tagline:
        'Agents collaborate by reading and writing the ontology — no hard-coded inter-agent protocol.',
      triggerBadge: 'Agent post',
      outputBadge: 'Emergent task',
      scenarios: [
        { industry: 'DevOps', example: 'Triage agent posts symptoms; diagnosis agent adds hypotheses; fix-suggester proposes patches.' },
        { industry: 'Research', example: 'Literature, dataset, and experiment agents share findings as ontology nodes.' },
        { industry: 'Trading', example: 'Sentiment, risk, and execution agents coordinate through the shared portfolio graph.' },
        { industry: 'Customer ops', example: 'Triage, KYC, refund, and communications agents collaborate on the ticket graph.' },
      ],
      interactionNotes: [
        'Agents subscribe to graph deltas instead of point-to-point messaging.',
        'New agents plug in by declaring their input/output node types.',
        'Human-in-the-loop is a special kind of agent — same protocol.',
      ],
    },
    {
      id: 'risk-scoring',
      number: '09',
      group: 'output',
      title: 'Risk Scoring',
      tagline:
        'Aggregate multi-source rules into a single score for an entity; recompute the whole graph when anything moves.',
      triggerBadge: 'Any input change',
      outputBadge: 'Score',
      scenarios: [
        { industry: 'Finance', example: 'Customer / counterparty / loan risk scores with full attribution.' },
        { industry: 'Procurement', example: 'Supplier risk index across financial, operational, and ESG dimensions.' },
        { industry: 'Energy', example: 'Equipment-health score from corrosion + vibration + age + recent-outage history.' },
        { industry: 'Healthcare', example: 'Patient acuity scoring across vitals, labs, and comorbidities.' },
      ],
      interactionNotes: [
        'The score is always explainable — every contributor is one click away.',
        'Reactive — nudge any input, see the score and its contributors update.',
        'Different audiences see the same score expressed in different units (cost, lives, days).',
      ],
    },
    {
      id: 'explainability',
      number: '10',
      group: 'output',
      title: 'Audit Trail / Explainability',
      tagline:
        'Given any decision, expand it backwards into the full chain of goals, objects, inferences, constraints, and data sources.',
      triggerBadge: 'Post-decision query',
      outputBadge: 'Causal chain',
      scenarios: [
        { industry: 'Energy', example: '"Why did we ban Plan 53A for this pump?" → cites the standard + the operating-condition reading.' },
        { industry: 'Finance', example: '"Why was this loan denied?" → rule + KYC fields + collateral graph.' },
        { industry: 'Healthcare', example: '"Why was drug Y not prescribed?" → contraindication rule + interaction graph + guideline citation.' },
        { industry: 'Regulatory', example: 'Anything a regulator can ask, the ontology can answer with citations and clickable provenance.' },
      ],
      interactionNotes: [
        'Available for every past decision the ontology produced — not just the current one.',
        'Output is graph + text + cited data sources, all clickable.',
        'The same chain reverse-feeds into governance reviews — "this rule contradicts that one".',
      ],
    },
  ],
}
